import {getTotalsBook, getSpreadBook} from '../../../utils/betting';

const periodKeys = {
    "FG": "full",
    "FH": "1st-half",
    "SH": "2nd-half",
    "1": "1st-qtr"
};

// sportAdapters/nbaAdapter.js
const nbaAdapter = {
    // Defaults for this sport
    defaultChartType: "quarter-points",
    defaultSubSelection: "FG",
    
    // Endpoint construction
    getScoresEndpoint: (game) => `gateway/nfl-gamescores?home=${game.home.team_id}&away=${game.away.team_id}`,
    
    // Data processing
    processScoresData: (scoresData) => {
        const initializeGamesData = () => {
            const keys = ["FG", "FH", "SH", "1", "2", "3", "4"];
            return keys.reduce((acc, inning) => {
                acc[inning] = {
                    data: [],
                    results: []
                };
                return acc;
            }, {});
        };

        const teams = Object.keys(scoresData);
        const tmpGamesData = teams.reduce((acc, team) => {
            acc[team] = initializeGamesData();
            return acc;
        }, {});

        const updateGamesObj = (obj, key, team1, val1, team2, val2) => {
            // add values to object
            obj[team1][key].data.push(val1);
            obj[team2][key].data.push(val2);
            // update results for individual innings
            if (val1 > val2) {
                obj[team1][key].results.push(1);
                obj[team2][key].results.push(0);
            } else if (val1 < val2) {
                obj[team1][key].results.push(0);
                obj[team2][key].results.push(1);
            } else {
                obj[team1][key].results.push(2);
                obj[team2][key].results.push(2);
            };
        };

        /**
         * quarters: { [period: string]: number }
         *
         * Keys "1"‑"4" = regulation quarters
         * Any other key  = overtime (rolled into second‑half total only)
         */
        function getTeamScoresBreakdown(quarters) {
            const result = {
                fhTotal: 0,
                shTotal: 0,
                q1Total: 0,
                q2Total: 0,
                q3Total: 0,
                q4Total: 0,
            };

            let total = 0;
                    
            for (const [k, v] of Object.entries(quarters)) {
                switch (k) {
                    case "1":
                        result.fhTotal += v;
                        result.q1Total += v;
                        total += v;
                        break;
                    case "2":
                        result.fhTotal += v;
                        result.q2Total += v;
                        total += v;
                        break;
                    case "3":
                        result.q3Total += v;
                        total += v;
                        break;
                    case "4":
                        result.q4Total += v;
                        total += v;
                        break;
                    default:            
                        break;
                }
            }
            
            // older games don't have OT - calculate from half total and total score
            result.shTotal = total - result.fhTotal;

            return result;
        }
  
        const processGameSet = (obj, team1Game, team1, team2Game, team2) => {
            // full game data
            const t1FG = team1Game['team_score'];
            const t2FG = team2Game['team_score'];
            updateGamesObj(obj, "FG", team1, t1FG, team2, t2FG);
            
            // Game period breakdown
            const t1Scores = getTeamScoresBreakdown(team1Game['team_quarters']);
            const t2Scores = getTeamScoresBreakdown(team2Game['team_quarters']);
            updateGamesObj(obj, "FH", team1, t1Scores.fhTotal, team2, t2Scores.fhTotal);
            updateGamesObj(obj, "SH", team1, t1Scores.shTotal, team2, t2Scores.shTotal);
            updateGamesObj(obj, "1", team1, t1Scores.q1Total, team2, t2Scores.q1Total);
            updateGamesObj(obj, "2", team1, t1Scores.q2Total, team2, t2Scores.q2Total);
            updateGamesObj(obj, "3", team1, t1Scores.q3Total, team2, t2Scores.q3Total);
            updateGamesObj(obj, "4", team1, t1Scores.q4Total, team2, t2Scores.q4Total);

        };
        
        // get data array for each team
        const minLength = Math.min(scoresData[teams[0]].length, scoresData[teams[1]].length);
        for (let i = 0; i < minLength; i++){
            const t1Game = scoresData[teams[0]][i];
            const t2Game = scoresData[teams[1]][i];
            processGameSet(tmpGamesData, t1Game, teams[0], t2Game, teams[1]);
        };
         
        return { gamesData: tmpGamesData };
    },
    
    getTotalResult: (line, t1Score, t2Score) => {
        const diff = t1Score + t2Score - line;
        if(diff > 0){
            return 1;
        }else if(diff < 0){
            return 0; 
        }else{
            return 2; 
        };
    },

    getSpreadResult: (t1Runline, t1Score, t2Score) => {
        const t1Diff = t1Score + t1Runline - t2Score;
        // Note: returned array is [t1 result, t2 result]
        if(t1Diff > 0){
            return {
                tResults: [1, 0],
                't1Diff': t1Diff
            };
        }else if(t1Diff < 0){
            return {
                tResults: [0, 1],
                't1Diff': t1Diff
            }; 
        }else{
            return {
                tResults: [2, 2],
                't1Diff': t1Diff
            }; 
        };
    },

    // Calculate betting results
    calculateBettingResults: function(gamesData, game, chartType, periodSelection) {
        // NFL-specific betting line calculations
        const teams = Object.keys(gamesData);
        const t1 = teams[0];
        const t2 = teams[1];
        const t1Scores = gamesData[t1][periodSelection].data;
        const t2Scores = gamesData[t2][periodSelection].data;

        // Get key that maps to odds records for the period selection
        const periodKey = periodKeys[periodSelection];
        let data = {};

        if(chartType === "totals"){
            const totalsBook = getTotalsBook(game, 'nfl', periodKey, 'first'); // get first book's most recent totals line
            data = {
                line: totalsBook?.line || null,
                results: []
            };
        }

        if(chartType === "spread"){
            const spreadBook = getSpreadBook(game, 'nfl', periodKey, 'first'); // get first book's most recent spread line
            data = teams.reduce((acc, team) => {
                acc[team] = {
                    spread: team === game?.home?.team_id
                        ? spreadBook?.home?.points || null
                        : spreadBook?.away?.points || null,
                    data: [],
                    results: []
                };
                return acc;
            }, {});
        }

        // Process the historical data to calculate results
        const minLength = Math.min(t1Scores.length, t2Scores.length);
        for (let i = 0; i < minLength; i++){
            const t1Pts = t1Scores[i];
            const t2Pts = t2Scores[i];

            if(chartType === "totals"){
                data.results.push(this.getTotalResult(data.line, t1Pts, t2Pts));
            }
            if(chartType === "spread"){
                const spreadRes = this.getSpreadResult(data[t1].spread, t1Pts, t2Pts);
                data[t1].results.push(spreadRes.tResults[0]);
                data[t1].data.push(spreadRes.t1Diff);
                data[t2].results.push(spreadRes.tResults[1]);
                data[t2].data.push((spreadRes.t1Diff * -1));
            }
        };

        return data;
    },
    
    // Generate results for different chart types
    generateResults: ({ chartType, periodSelection, teamSelection, gamesData, bettingLineResults, getWLSets, getAverages }) => {
        const teams = Object.keys(gamesData);
        let results = {
            type: chartType,
            period: null,
            team: null
        };
        
        switch(chartType) {
            case 'quarter-points':
                results.period = periodSelection;
                // get data array and results for each team
                teams.forEach(team => {
                    const data = gamesData[team][periodSelection].data; // team pts data
                    const teamResults = gamesData[team][periodSelection].results; 
                    const wl = getWLSets(teamResults);
                    const avgs = getAverages(data);
                    results[team] = {
                        last5: { results: wl[0], average: avgs[0] },
                        last10: { results: wl[1], average: avgs[1] },
                        last15: { results: wl[2], average: avgs[2] },
                        last20: { results: wl[3], average: avgs[3] },
                        last30: { results: wl[4], average: avgs[4] }
                    };
                });

                break;

            case 'totals':
                results.period = periodSelection;
                const team1 = teams[0];
                const team2 = teams[1];
                // get data array for each team
                let teamTotalAvgs = {};
                // get team avgs
                teams.forEach(team => {
                    const teamPts = gamesData?.[team]?.[periodSelection]?.data || []; 
                    teamTotalAvgs[team] = getAverages(teamPts);
                });
                // get win loss data 
                const totalWL = getWLSets(bettingLineResults?.results || []);
                // update results data
                results.last5 = { results: totalWL[0], average: { [team1]: teamTotalAvgs[team1][0], [team2]: teamTotalAvgs[team2][0]} }
                results.last10 = { results: totalWL[1], average: { [team1]: teamTotalAvgs[team1][1], [team2]: teamTotalAvgs[team2][1]} }
                results.last15 = { results: totalWL[2], average: { [team1]: teamTotalAvgs[team1][2], [team2]: teamTotalAvgs[team2][2]} }
                results.last20 = { results: totalWL[3], average: { [team1]: teamTotalAvgs[team1][3], [team2]: teamTotalAvgs[team2][3]} }
                results.last30 = { results: totalWL[4], average: { [team1]: teamTotalAvgs[team1][4], [team2]: teamTotalAvgs[team2][4]} }

                break;

            case 'spread':
                results.period = periodSelection;
                results.team = teamSelection;

                const teamSpreadData = bettingLineResults?.[teamSelection];
                // get win loss data 
                const spreadWL = getWLSets(teamSpreadData?.results || []);
                const teamAvgs = getAverages(teamSpreadData?.data || []);
                // update results data
                results.last5 = { results: spreadWL[0], average: teamAvgs[0] }
                results.last10 = { results: spreadWL[1], average: teamAvgs[1] }
                results.last15 = { results: spreadWL[2], average: teamAvgs[2] }
                results.last20 = { results: spreadWL[3], average: teamAvgs[3] }
                results.last30 = { results: spreadWL[4], average: teamAvgs[4] }
                break;

            default:
                break;
        }
        
        return results;
    },
    
    // Determine if results need updating
    shouldUpdateResults: ({ chartType, periodSelection, teamSelection, gamesData, bettingLineResults, resultSets, periodDropdownOptions, teamDropdownOptions }) => {
        let shouldUpdate = false;

        // NBA-specific logic to determine if results need updating
        switch(chartType) {
            case 'quarter-points':
                if( !gamesData || 
                    !periodDropdownOptions ||
                    !periodSelection || 
                    !periodDropdownOptions.some(obj => obj.key === periodSelection) ||
                    (resultSets && resultSets.type === 'quarter-points' && resultSets.period === periodSelection) 
                ){
                    shouldUpdate = false;
                }else{
                    shouldUpdate = true;
                };
                break;

            case 'totals': 
                if(!bettingLineResults || !gamesData || (resultSets && resultSets.type === 'totals' && resultSets.period === periodSelection)){
                    shouldUpdate = false;
                }else{
                    shouldUpdate = true;
                };

                break;

            case 'spread':
                if( !bettingLineResults || 
                    !teamDropdownOptions ||
                    !teamSelection || 
                    !teamDropdownOptions.some(obj => obj.key === teamSelection) ||
                    (resultSets && resultSets.type === 'spread' && resultSets.team === teamSelection && resultSets.period === periodSelection) 
                ){
                    shouldUpdate = false;
                }else{
                    shouldUpdate = true;
                };

                break;

            default:
                break;
        }
        
        return shouldUpdate;
    },
    
    formatQuarterPoints: ({ labels, numGames, gamesData, getTeamColors, periodSelection}) => {
        const teams = Object.keys(gamesData);       
        // init chart datasets for each team
        const teamData = teams.reduce((acc, team) => {
            const teamColors = getTeamColors(team);
            acc[team] = {
                label: team,
                data: [],
                borderWidth: 1,
                backgroundColor: teamColors.color,
                borderColor: teamColors.border,
            };
            return acc;
        }, {});
                    
        // get data array and results for each team
        teams.forEach(team => {
            const data = gamesData[team][periodSelection].data; // team pts data
            const teamPts = data.slice(-numGames);
            teamData[team].data = (teamPts);
        });

        return {
            chartLabel: 'Points Scored By Period',
            horizontalLine: null,
            chartData: {
                labels,
                datasets: Object.values(teamData),
            },
            chartOptions: {
                scales: {
                    y: { beginAtZero: true },
                    x: { grid: { display: false } },
                },
                plugins: {  
                    htmlLegend: { containerID: 'legend-container' },
                    legend: { display: false },
                    title: { display: false },
                }
            },
        };

    },

    formatTotalPointsData: ({ labels, numGames, gamesData, bettingLineResults, getTeamColors, periodSelection }) => {
        const teams = Object.keys(gamesData);
        // init chart datasets for each team
        const teamData = teams.reduce((acc, team) => {
            const teamColors = getTeamColors(team);
            acc[team] = {
            label: team,
            data: [],
            borderWidth: 1,
            backgroundColor: teamColors.color,
            borderColor: teamColors.border,
            };
            return acc;
        }, {});
        
        // get data array for each team
        teams.forEach(team => {
            const teamPts = gamesData[team][periodSelection].data; 
            const ptsSlice = teamPts.slice(-numGames);
            teamData[team].data = (ptsSlice);
        });

        return {
            chartLabel: 'Combined Team Points',
            horizontalLine: bettingLineResults.line,
            chartData: { labels, datasets: Object.values(teamData) },
            chartOptions: {
                scales: {
                    y: { beginAtZero: true, stacked: true },
                    x: { stacked: true, grid: { display: false } },
                },
                plugins: {
                    htmlLegend: { containerID: 'legend-container' },
                    legend: { display: false },
                    title: { display: false },
                }
            },
        };
    },

    formatSpreadData: ({ labels, numGames, bettingLineResults, chartColors, teamSelection, game }) => {
        // prepare team data structure
        let teamData = {
            data: new Array(numGames),
            borderWidth: 1,
            backgroundColor: new Array(numGames), 
            borderColor: new Array(numGames)
        };

        //  data for label and spread value
        const oppTeam = (teamSelection === game.home.team_id) ? game.away.team_id : game.home.team_id;
        const spread = bettingLineResults[teamSelection]?.spread;
        const chartLabel = `${teamSelection} ${spread > 0 ? `+${spread}` : spread} vs. ${oppTeam}`;

        // get both team games
        const teamSpreadData = bettingLineResults[teamSelection]?.data?.slice(-numGames);
        for (let i = 0; i < teamSpreadData?.length || 0; i++) {
            // calc score w/ spread and add to data array
            const score = teamSpreadData[i];
            teamData.data[i] = score;
            // color bar based on score differential 
            if( score > 0 ){
                teamData.backgroundColor[i] = chartColors.winColor;
                teamData.borderColor[i] = chartColors.winBorder;
            }else{
                teamData.backgroundColor[i] = chartColors.awayColor;
                teamData.borderColor[i] = chartColors.awayBorderColor;
            };
        };

        return {
            chartLabel: chartLabel,
            horizontalLine: null,
            chartData: { labels, datasets: [teamData] },
            chartOptions: {
                responsive: true,
                scales: {
                    y: { beginAtZero: true },
                    x: { grid: { display: false } },
                },
                plugins: {
                    htmlLegend: { containerID: 'legend-container' },
                    legend: { display: false },
                    title: { display: false },
                },
            },
        };      
    },

    // Format chart data based on chart type
    formatChartData: function({ chartType, periodSelection, teamSelection, numGames, gamesData, bettingLineResults, getTeamColors, chartColors, game }) {
        let formattedData = {};
        const labels = Array.from({ length: numGames }, (_, i) => numGames - i);

        // NBA-specific chart data formatting
        switch(chartType) {
            case 'quarter-points':
                if(!periodSelection) return null;
                formattedData = this.formatQuarterPoints({labels, numGames, gamesData, getTeamColors, periodSelection});
                break;

            case 'totals':
                if(!periodSelection) return null;
                formattedData = this.formatTotalPointsData({labels, numGames, gamesData, bettingLineResults, getTeamColors, periodSelection });
                break;
            
            case 'spread':
                formattedData = this.formatSpreadData({labels, numGames, bettingLineResults, chartColors, teamSelection, game });
                break;

            default:
                break;
        }
        
        return formattedData;
    },
    
    // Get chart type options
    getChartTypeOptions: () => [
        { value: "quarter-points", label: "Points Scored By Period" },
        { value: "totals", label: "Total Points" },
        { value: "spread", label: "Team Spread" }
    ],
    
    // Get period dropdown options based on chart type
    getPeriodDropdownOptions: (chartType, game) => {
        if (chartType === "quarter-points") {
            return {
                options: [
                    { key: "FG", value: "Full Game" },
                    { key: "FH", value: "First Half" },
                    { key: "SH", value: "Second Half" },
                    { key: "1", value: "1st Quarter" },
                    { key: "2", value: "2nd Quarter" },
                    { key: "3", value: "3rd Quarter" },
                    { key: "4", value: "4th Quarter" }
                ],
                defaultSelection: "FG",
                forceDefault: false
            };
        } else if (chartType === "spread" && game) {
            return {
                options: [
                    { key: "FG", value: "Full Game" },
                    { key: "FH", value: "First Half" },
                    { key: "SH", value: "Second Half" },
                    { key: "1", value: "1st Quarter" },                
                ],
                defaultSelection: "FG",
                forceDefault: true
            };
        }else if(chartType === "totals"){
            return {
                options: [
                    { key: "FG", value: "Full Game" },
                    { key: "FH", value: "First Half" },
                    { key: "SH", value: "Second Half" },
                    { key: "1", value: "1st Quarter" },                
                ],
                defaultSelection: "FG",
                forceDefault: true
            };
        }
    },

    // Get period dropdown options based on chart type
    getTeamDropdownOptions: (chartType, game) => {
        if (chartType === "spread" && game) {
            return {
                options: [
                    { key: game?.home?.team_id, value: game?.home?.team_id },
                    { key: game?.away?.team_id, value: game?.away?.team_id }
                ],
                defaultSelection: game?.home?.team_id,
                forceDefault: true
            };
        }
    }
};

export default nbaAdapter;